import {
  LayoutDashboard, Users, UserCheck, Archive, FileText, LifeBuoy, Activity,
  Folder, CalendarDays, BarChart3, Settings, ShieldAlert, User as UserIcon,
  Heart, Star, Edit, Eye, CreditCard, Bell, MessageSquare, Briefcase,
  Share2, Gift, Image, Film,
} from 'lucide-react';

/**
 * Role-keyed onboarding content — intro panel bullets + guided tour steps.
 * Every tour step references an existing `data-testid` selector on the page,
 * so we never hard-code DOM structure.
 */

export const onboardingContent = {
  // ─── ADMIN ──────────────────────────────────────────────
  admin: {
    intro: {
      title: "Welcome to the Admin Dashboard",
      subtitle: "You're looking at the single source of truth for the platform — users, approvals, content, billing and analytics all live here. This tour takes ~60 seconds and you can replay it anytime from the Settings tab.",
      bullets: [
        { icon: LayoutDashboard, label: 'Overview',    description: 'Live platform metrics and recent activity at a glance.' },
        { icon: Users,           label: 'Users',       description: 'Search, edit, suspend or archive any account.' },
        { icon: UserCheck,       label: 'Approvals',   description: 'Review and approve new signups or events.' },
        { icon: Archive,         label: 'Archived',    description: 'Restore or permanently delete archived accounts.' },
        { icon: LifeBuoy,        label: 'Support',     description: 'Triage and resolve user support tickets.' },
        { icon: Folder,          label: 'Content',     description: 'Manage branding, banners, news and demo mode.' },
      ],
    },
    tour: [
      {
        selector: '[data-testid="tab-overview"]',
        title: 'Overview — your daily pulse check',
        body: 'Start here every morning. Top cards show Active Subs, MRR, Churn and New Signups. Further down you\'ll find role distribution and application funnel charts.',
      },
      {
        selector: '[data-testid="tab-users"]',
        title: 'Users — manage every account',
        body: 'Every account on the platform lives here. Search or filter by role, then use the card actions to Edit, Log in as them, Suspend, or Archive. Archive is reversible — it soft-deletes so nothing is ever lost.',
      },
      {
        selector: '[data-testid="tab-archived"]',
        title: 'Archived — the soft delete safety net',
        body: 'Archived accounts are hidden from the platform but fully recoverable here. Users can also request reactivation themselves — those requests appear in the Approvals tab.',
      },
      {
        selector: '[data-testid="tab-approvals"]',
        title: 'Approvals — keep the funnel moving',
        body: 'New signups + pending events land here. Tick multiple users to bulk-approve from the sticky action bar. The number badge on the tab shows how many are waiting.',
      },
      {
        selector: '[data-testid="tab-support"]',
        title: 'Support — inbound user issues',
        body: 'Every support message users submit lands here. Acknowledge, reply, and resolve when done. An automatic welcome reply now fires on every new ticket so users never stare at silence.',
      },
      {
        selector: '[data-testid="tab-status"]',
        title: 'Status — system health',
        body: 'Services up/down, queue depths, background job health. If anything stops processing (emails, exports, scheduled backups), you\'ll see it here first.',
      },
      {
        selector: '[data-testid="tab-content"]',
        title: 'Content — branding, news, tools',
        body: 'Control site-wide branding, promo banners, news posts, the Site Tool Kit (Demo Mode / Subscription QA / Maintenance), and the new Import / Export + Google Drive Backup. These tools affect what real users see — use with care.',
      },
      {
        selector: '[data-testid="tab-events"]',
        title: 'Events — platform-wide events',
        body: 'Workshops, screenings, meet-ups submitted by industry services + any you publish directly. Approve, feature, or decline from here.',
      },
      {
        selector: '[data-testid="tab-analytics"]',
        title: 'Analytics — deeper insights',
        body: 'Leaderboards, role-by-role trends, engagement heatmaps, retention curves. Great for weekly updates to stakeholders and for spotting which roles need attention.',
      },
      {
        selector: '[data-testid="admin-activity-drawer-trigger"]',
        title: 'Activity drawer — what changed today',
        body: 'Click the activity icon anytime to see every signup, approval, moderation action and support ticket in chronological order. The unread badge tells you what\'s new since your last visit.',
      },
      {
        selector: '[data-testid="tab-settings"]',
        title: 'Settings — platform configuration',
        body: 'Email (SMTP), notification preferences, and general platform settings. You can also replay this tour from the "Show help" button in the header anytime.',
      },
    ],
    helpIcons: {
      usersSearch: {
        title: 'Search users',
        body: 'Search by name, email or username. Matches are live — as you type, the grid filters. Use the role and status dropdowns to narrow results further.',
      },
      archiveVsSuspend: {
        title: 'Archive vs Suspend',
        body: 'Suspend locks an account immediately (user sees a blocked screen). Archive soft-deletes — the user disappears from the platform but is fully recoverable from the Archived tab. Neither action is destructive.',
      },
      approvalsBulk: {
        title: 'Bulk approve / reject',
        body: 'Tick multiple pending users then use the sticky action bar at the top. "Select all N matching" picks everything that matches your current filter, even across pages.',
      },
      siteToolKit: {
        title: 'Site Tool Kit — power tools',
        body: 'Demo Mode hides seeded demo profiles from public directories — useful for a clean launch. Subscription QA lets you simulate billing events. Maintenance mode takes the whole site offline. All changes are logged.',
      },
      supportSLA: {
        title: 'Support SLAs',
        body: 'A red chip appears on any ticket that\'s been open for more than 48 hours. Staying under that threshold keeps the SLA dashboard green on the Analytics tab.',
      },
    },
  },

  // ─── TALENT ─────────────────────────────────────────────
  talent: {
    intro: {
      title: 'Welcome to your Talent dashboard',
      subtitle: "This is where you manage how you're discovered, apply for casting calls, and track every response. Take 30 seconds to see how it fits together.",
      bullets: [
        { icon: LayoutDashboard, label: 'Overview',      description: 'See applications, saved items and profile performance.' },
        { icon: Edit,            label: 'Edit Profile',  description: 'Your headshots, bio, skills, credits and reel.' },
        { icon: Briefcase,       label: 'Applications',  description: 'Everywhere you\'ve applied, with status.' },
        { icon: CalendarDays,    label: 'Planner',       description: 'Your audition diary — book 40% more with consistent use.' },
        { icon: Heart,           label: 'Saved Items',   description: 'Casting calls, agents or services you\'ve bookmarked.' },
        { icon: Share2,          label: 'Promote Profile', description: 'Ready-to-post captions and links to promote your profile on social.' },
      ],
    },
    tour: [
      { selector: '[data-testid="tab-overview"]',     title: 'Overview — your snapshot', body: 'Applications, saves, shortlisted count, and your profile performance trend over the last 14 days.' },
      { selector: '[data-testid="overview-stats"]',   title: 'Your key numbers', body: 'Click any card to jump straight to that tab — quick access to applications, saved items, shortlists and pending responses.' },
      { selector: '[data-testid="public-profile-card"]',      title: 'Your public profile link', body: 'Copy this link or share it — it\'s how casting directors and the public find you outside the platform. The Promote Profile tool has ready-to-post captions too.' },
      { selector: '[data-testid="tab-profile"]',      title: 'Edit Profile — what gets you cast', body: 'A complete, up-to-date profile ranks higher in searches. The Profile Completion tracker above shows you exactly what\'s missing.' },
      { selector: '[data-testid="tab-applications"]', title: 'Applications — your pipeline', body: 'Every casting call you\'ve applied to, grouped by status (Pending, Shortlisted, Accepted, Rejected). Click any row for the full detail.' },
      { selector: '[data-testid="tab-saved"]',        title: 'Saved Items', body: 'Anything you bookmark across the platform lives here — casting calls, agents, services. Nothing is shared publicly.' },
      { selector: '[data-testid="tab-planner"]',      title: 'Planner — your audition diary',
        body: 'This is where your career lives day-to-day. Track auditions, callbacks, shoot dates, self-tape deadlines, and preparation tasks. Set reminders before every audition so nothing slips — planners who log every audition book 40% more jobs than those who don\'t.' },
      { selector: '[data-testid="tab-settings"]',     title: 'Settings — billing, notifications, referrals', body: 'Subscription tier, password, messaging preferences, email digest and your referral program link — all in one place.' },
    ],
    helpIcons: {
      profileCompletion: {
        title: 'Why profile completion matters',
        body: 'Profiles over 80% complete rank higher in casting director searches and are 3x more likely to receive messages. Each unfilled section costs you visibility.',
      },
      shareKit: {
        title: 'Promote Profile',
        body: 'Pre-written captions and images you can post on Instagram, LinkedIn or X. Sharing a well-filled profile typically doubles inbound message rate.',
      },
      referrals: {
        title: 'Referral program',
        body: 'Invite a fellow performer and you both get 14 free Pro days once their account is approved. Your unique link tracks everything automatically.',
      },
    },
  },

  // ─── CASTING PROFESSIONAL ───────────────────────────────
  casting_professional: {
    intro: {
      title: 'Welcome to your Casting Pro dashboard',
      subtitle: "Post casting calls, review applicants and find the right talent fast. Here's the 30-second tour.",
      bullets: [
        { icon: LayoutDashboard, label: 'Overview',     description: 'Listings, applicants, shortlists at a glance.' },
        { icon: FileText,        label: 'Listings',     description: 'Create, edit and close casting calls.' },
        { icon: Users,           label: 'Talent search',description: 'Browse the full talent directory.' },
        { icon: CalendarDays,    label: 'Planner',      description: 'Schedule auditions, callbacks and casting days.' },
        { icon: Heart,           label: 'Saved',        description: 'Profiles and listings you\'ve bookmarked.' },
        { icon: CreditCard,      label: 'Billing',      description: 'Plan and payment details.' },
      ],
    },
    tour: [
      { selector: '[data-testid="tab-overview"]',  title: 'Overview — your casting pipeline', body: 'See how many active listings you have, applicants received, shortlists and saved talent.' },
      { selector: '[data-testid="overview-stats"]',title: 'Key numbers', body: 'Click any card to jump to the relevant tab. Shortlisted is the applicants you\'ve marked as a yes.' },
      { selector: '[data-testid="new-casting-btn"]',title: 'Post a casting call', body: 'Create a new call from the top-right button anytime. Include clear role requirements to attract the right talent.' },
      { selector: '[data-testid="tab-listings"]',  title: 'Listings — manage your calls', body: 'Edit, close or extend any casting call. Each listing shows live applicant counts and lets you shortlist in one click.' },
      { selector: '[data-testid="tab-saved"]',     title: 'Saved — your longlist', body: 'Every talent profile and listing you bookmark lives here. Great for assembling callback lists.' },
      { selector: '[data-testid="tab-planner"]',   title: 'Planner — run your casting sessions',
        body: 'Schedule auditions, casting days, callbacks, and Zoom sessions in one calendar. Link each event to the listing it belongs to, assign it to team members, and see who\'s available before you invite talent. The Planner is what turns a flood of applicants into a well-run shoot.' },
      { selector: '[data-testid="tab-settings"]',  title: 'Settings & billing', body: 'Subscription tier, password, notification preferences and messaging controls — all here.' },
    ],
    helpIcons: {
      postCastingCall: {
        title: 'Writing a great casting call',
        body: 'Clear role, compensation, location, shoot dates and a brief brand / tone. Listings with all fields completed get 2.5x more quality applicants.',
      },
      shortlist: {
        title: 'Shortlist workflow',
        body: 'Mark applicants as Shortlisted when they pass first review, Accepted once cast, or Rejected to close the loop. Talent see their status update in real time.',
      },
    },
  },

  // ─── PRODUCTION COMPANY ─────────────────────────────────
  production_company: {
    intro: {
      title: 'Welcome to your Production Company dashboard',
      subtitle: 'Post casting calls, manage projects, and find crew or talent. This is your production hub.',
      bullets: [
        { icon: LayoutDashboard, label: 'Overview',     description: 'Listings, applicants and activity overview.' },
        { icon: FileText,        label: 'Listings',     description: 'Post and manage casting calls.' },
        { icon: Users,           label: 'Talent',       description: 'Browse the full talent directory.' },
        { icon: Briefcase,       label: 'Crew',         description: 'Connect with crew for your productions.' },
        { icon: CalendarDays,    label: 'Planner',      description: 'Schedule shoots and auditions.' },
        { icon: MessageSquare,   label: 'Messages',     description: 'Direct chat with applicants.' },
      ],
    },
    tour: [
      { selector: '[data-testid="tab-overview"]',  title: 'Overview — project pulse', body: 'Active listings, applicants and recent activity across all your productions.' },
      { selector: '[data-testid="new-casting-btn"]',title: 'Post a casting call', body: 'Create a new call anytime. Include production details for higher-quality applications.' },
      { selector: '[data-testid="tab-listings"]',  title: 'Listings — your casting calls', body: 'Manage every open and closed call. Shortlist, message, or mark cast directly from each listing.' },
      { selector: '[data-testid="tab-planner"]',   title: 'Planner — your production command centre',
        body: 'The Planner is where every shoot comes together: call sheets, audition slots, shoot days, crew availability, location recces, and deliverable deadlines — all on one calendar your whole team can see. Link events to listings, tag talent, and share a clean read-only view with your crew. Productions that run the Planner actively cut last-minute rescheduling by ~50%.' },
      { selector: '[data-testid="tab-settings"]',  title: 'Settings & billing', body: 'Company profile details, subscription tier, notifications and billing.' },
    ],
    helpIcons: {
      plannerTasks: {
        title: 'Planner tasks',
        body: 'Create tasks linked to specific shoot dates or auditions. Tasks can be assigned to team members and show on the calendar view.',
      },
    },
  },

  // ─── AGENT ──────────────────────────────────────────────
  agent: {
    intro: {
      title: 'Welcome to your Agent dashboard',
      subtitle: 'Manage your represented talent, respond to casting calls, and grow your roster. Quick 30-second tour:',
      bullets: [
        { icon: LayoutDashboard, label: 'Overview',        description: 'Roster size, active calls and activity.' },
        { icon: Users,           label: 'Talent Roster',   description: 'Your represented talent — invite or remove.' },
        { icon: FileText,        label: 'Casting Calls',   description: 'Submit your talent to open calls.' },
        { icon: CalendarDays,    label: 'Planner',         description: 'Every client\'s auditions, callbacks and jobs.' },
        { icon: MessageSquare,   label: 'Messages',        description: 'Talk directly with casting directors.' },
        { icon: CreditCard,      label: 'Billing',         description: 'Plan and subscription management.' },
      ],
    },
    tour: [
      { selector: '[data-testid="tab-overview"]',     title: 'Overview — agency pulse', body: 'Represented talent count, saved items, profile field completeness and media uploaded.' },
      { selector: '[data-testid="tab-roster"]',       title: 'Talent Roster — your clients', body: 'Invite new talent to your roster, manage existing relationships, and see their profile stats at a glance.' },
      { selector: '[data-testid="public-profile-card"]', title: 'Your agency public profile', body: 'This is how talent discover you. Keep specialisms, logo and bio up to date — it directly drives roster enquiries.' },
      { selector: '[data-testid="tab-saved"]',        title: 'Saved items', body: 'Casting calls, talent and industry services you\'ve bookmarked — useful for scouting and research.' },
      { selector: '[data-testid="tab-planner"]',      title: 'Planner — keep your roster moving',
        body: 'The nerve centre of an agent\'s week. Track every client\'s auditions, callbacks and confirmed jobs on one calendar. Set reminders so you never miss a submission deadline, colour-code by client, and pull a quick view of who\'s busy vs. who needs more auditions this week. Agents who log every audition submit 30% more talent per month.' },
      { selector: '[data-testid="tab-settings"]',     title: 'Settings & billing', body: 'Agency details, subscription tier, notifications and team preferences.' },
    ],
    helpIcons: {
      inviteTalent: {
        title: 'Inviting talent to your roster',
        body: 'Send an invite link to any talent — they receive an email and can accept with one click. You can set commission terms during the invite.',
      },
    },
  },

  // ─── INDUSTRY SERVICE ───────────────────────────────────
  industry_service: {
    intro: {
      title: 'Welcome to your Industry Service dashboard',
      subtitle: "Showcase your service, post events, and connect with clients across the industry. Here's what you can do:",
      bullets: [
        { icon: LayoutDashboard, label: 'Overview',     description: 'Profile completion, saved items and status.' },
        { icon: Edit,            label: 'Edit Profile', description: 'Your services, portfolio and bio.' },
        { icon: CalendarDays,    label: 'Events',       description: 'Post workshops, screenings, meet-ups.' },
        { icon: Film,            label: 'Planner',      description: 'Track client bookings, consultations, sessions.' },
        { icon: Heart,           label: 'Saved',        description: 'Bookmarked casting calls, talent, services.' },
        { icon: MessageSquare,   label: 'Messages',     description: 'Conversations with clients.' },
      ],
    },
    tour: [
      { selector: '[data-testid="tab-overview"]',     title: 'Overview — your visibility pulse', body: 'Profile completeness, saved items and publish status. The trend chart shows your profile performance over 14 days.' },
      { selector: '[data-testid="public-profile-card"]', title: 'Your public service page', body: 'Clients find you via this URL. A clear primary category, strong logo and filled portfolio are the three things that most influence enquiries.' },
      { selector: '[data-testid="tab-profile"]',      title: 'Edit Profile — your showcase', body: 'Add services offered, pricing range, gallery images and bio. Published profiles appear in the public directory.' },
      { selector: '[data-testid="tab-events"]',       title: 'Events — drive your community', body: 'Post workshops, screenings and meet-ups. Events appear on the public Events page and can drive significant traffic to your profile.' },
      { selector: '[data-testid="tab-planner"]',      title: 'Planner — run your bookings',
        body: 'Track every client booking, consultation, workshop and session in one calendar. Block out unavailable time so clients only see real slots, link events to your services for easier invoicing later, and set automatic reminders 24h before each booking. Consistent planner use turns one-off clients into regulars.' },
      { selector: '[data-testid="tab-settings"]',     title: 'Settings & billing', body: 'Subscription tier, notification preferences, and account management.' },
    ],
    helpIcons: {},
  },

  // ─── CREW ───────────────────────────────────────────────
  crew: {
    intro: {
      title: 'Welcome to your Crew dashboard',
      subtitle: 'Showcase your technical skills and portfolio, get hired for productions. Short tour:',
      bullets: [
        { icon: LayoutDashboard, label: 'Overview',     description: 'Profile status, saved items, media.' },
        { icon: Edit,            label: 'Edit Profile', description: 'Departments, credits, showreel, portfolio.' },
        { icon: CalendarDays,    label: 'Planner',      description: 'Shoot days, prep days, availability blocks.' },
        { icon: Heart,           label: 'Saved',        description: 'Casting calls or productions you\'ve bookmarked.' },
        { icon: MessageSquare,   label: 'Messages',     description: 'Direct chat with producers and casting directors.' },
      ],
    },
    tour: [
      { selector: '[data-testid="tab-overview"]',     title: 'Overview — your visibility', body: 'Profile completeness, publish status and 14-day profile performance trend.' },
      { selector: '[data-testid="public-profile-card"]', title: 'Your public crew profile', body: 'Producers find you via this URL. A complete list of departments, credits and showreel links directly drives hiring enquiries.' },
      { selector: '[data-testid="tab-profile"]',      title: 'Edit Profile — your CV', body: 'Add departments (sound, camera, lighting, etc.), past credits, portfolio images and a reel. More detail = more enquiries.' },
      { selector: '[data-testid="tab-saved"]',        title: 'Saved items', body: 'Bookmark casting calls, productions, and services for easy access later.' },
      { selector: '[data-testid="tab-planner"]',      title: 'Planner — stay booked and on schedule',
        body: 'Crew live by the call sheet. Log every shoot day, prep day, wrap and travel day here, plus availability blocks so producers can see when you\'re free. Link events to the production they belong to and you\'ll have a clean year-end log of every job you worked. Consistent planner use is what makes you the reliable crew member producers call back.' },
      { selector: '[data-testid="tab-settings"]',     title: 'Settings', body: 'Subscription, notification preferences and account management.' },
    ],
    helpIcons: {},
  },
};

export function getOnboardingContent(role) {
  return onboardingContent[role] || null;
}

/**
 * Site-wide first-visit intros for non-dashboard pages.
 *
 * Each entry is a function returning `{ tourId, title, subtitle, bullets }`
 * (so we can tailor copy by role where it materially helps). Pages that don't
 * have a variant per role simply ignore the `role` arg.
 *
 * tourId naming convention: `page_<path-slug>` — namespaced under
 * `onboarding:<uid>:<tourId>_intro_dismissed` in localStorage so it can't
 * collide with the dashboard role-keyed tours.
 */
const pageIntroFactories = {
  '/casting-calls': (role) => ({
    tourId: 'page_casting_calls',
    title: 'Casting Calls',
    subtitle:
      role === 'casting_professional' || role === 'production_company'
        ? "Browse every open call on the platform. Useful for spotting gaps, pricing benchmarks, or posting something that stands out."
        : role === 'agent'
        ? "Every open call in one place. Submit the right talent from your roster with a single click once you've opened a listing."
        : "Every open casting call in the UK, updated live. Use the filters to narrow by role type, location, pay and dates, then open a listing to apply.",
    bullets: [
      { icon: FileText,  label: 'Filter fast',   description: 'Project type, role, location, pay, audition dates.' },
      { icon: Heart,     label: 'Save for later',description: 'Bookmark interesting calls with the heart icon.' },
      { icon: Briefcase, label: 'Apply in one go',description: 'Open a listing — submit with the profile you already have.' },
    ],
  }),

  '/talent': (role) => ({
    tourId: 'page_talent',
    title: 'Talent Directory',
    subtitle:
      role === 'casting_professional' || role === 'production_company' || role === 'agent'
        ? "Search every verified performer on the platform. Filter by physical, skill and availability — message, save or shortlist directly."
        : "Discover performers across the UK. Great for research, collaboration, or seeing how your own profile compares.",
    bullets: [
      { icon: Users, label: 'Deep filters', description: 'Location, age, height, ethnicity, accents, skills.' },
      { icon: Star,  label: 'Shortlist',    description: 'Save profiles to your longlist for later.' },
      { icon: Eye,   label: 'Verified only (optional)', description: 'Toggle to hide unverified profiles.' },
    ],
  }),

  '/casting-professionals': () => ({
    tourId: 'page_casting_professionals',
    title: 'Casting Professionals',
    subtitle:
      "Every casting director and casting professional on the platform. Check bios, active calls, and reach out when there's a fit.",
    bullets: [
      { icon: UserCheck, label: 'Verified people', description: 'See who\'s actively casting right now.' },
      { icon: Eye,       label: 'Full bios',       description: 'Credits, specialisms, current projects.' },
    ],
  }),

  '/services': () => ({
    tourId: 'page_services',
    title: 'Industry Services',
    subtitle:
      "Photographers, showreel editors, coaches, studios and more. Find the right supplier for your next shoot or self-tape.",
    bullets: [
      { icon: Briefcase, label: 'Filter by category', description: 'Photography, reel editing, coaching, studios, kit hire.' },
      { icon: Heart,     label: 'Save a shortlist',   description: 'Bookmark suppliers you might use again.' },
    ],
  }),

  '/agents': () => ({
    tourId: 'page_agents',
    title: 'Agents & Agencies',
    subtitle:
      "Find representation or see who's booking the jobs you want. Each agency page lists specialisms, current roster and contact rules.",
    bullets: [
      { icon: Briefcase, label: 'Specialisms',     description: 'TV/Film, commercial, voice, stage, kids, dancers, etc.' },
      { icon: UserCheck, label: 'Submission rules',description: 'See whether they accept open submissions before you write.' },
    ],
  }),

  '/companies': () => ({
    tourId: 'page_companies',
    title: 'Production Companies',
    subtitle:
      "Studios and production houses that hire across the industry. Useful for research, targeted outreach, or checking a company before applying.",
    bullets: [
      { icon: Briefcase, label: 'Active output', description: 'Recent projects and upcoming casts where shared.' },
      { icon: Heart,     label: 'Save',          description: 'Bookmark companies you\'d like to work with.' },
    ],
  }),

  '/crew': () => ({
    tourId: 'page_crew',
    title: 'Crew Directory',
    subtitle:
      "Camera, sound, lighting, costume, makeup, stunts — every department. Filter by role and region to build your next crew.",
    bullets: [
      { icon: Users,     label: 'Every department', description: 'Find crew for any role, filtered by region and availability.' },
      { icon: Briefcase, label: 'Credits + reel',   description: 'Full CVs with reels and past productions.' },
    ],
  }),

  '/events': () => ({
    tourId: 'page_events',
    title: 'Events',
    subtitle:
      "Industry workshops, screenings, meet-ups and open auditions. Discover what's on and grow your network.",
    bullets: [
      { icon: CalendarDays, label: 'Filter by type', description: 'Workshops, screenings, networking, masterclasses.' },
      { icon: MessageSquare,label: 'RSVP + share',   description: 'Book your place or share with a friend.' },
    ],
  }),

  '/messages': () => ({
    tourId: 'page_messages',
    title: 'Messages',
    subtitle:
      "Your conversations with other members. Start a new thread, reply from the dock, or share attachments and planner events.",
    bullets: [
      { icon: MessageSquare,label: 'Threads + groups', description: 'One-to-one or group conversations, all searchable.' },
      { icon: CalendarDays, label: 'Planner shares',   description: 'Quick-add auditions / sessions to your Planner from any chat.' },
      { icon: Bell,         label: 'Control who can DM',description: 'Messaging preferences live in Settings → Messaging.' },
    ],
  }),

  '/saved': () => ({
    tourId: 'page_saved',
    title: 'Saved Items',
    subtitle:
      "Everything you've bookmarked across the platform — casting calls, talent, agents, services, companies and events — in one place.",
    bullets: [
      { icon: Heart,    label: 'Heart icon saves',  description: 'Tap it on any card to keep it here.' },
      { icon: Folder,   label: 'Sorted by type',    description: 'Filter tabs split saved items by what they are.' },
    ],
  }),

  // NOTE: /pricing intentionally has no onboarding intro panel.
  // The pricing page now owns its own editorial hero + reassurance strip,
  // and a large dashboard-style info panel above it would fight with the
  // conversion-focused layout.
};

export function getPageIntro(pathname, role) {
  const factory = pageIntroFactories[pathname];
  return factory ? factory(role) : null;
}

/**
 * Return the list of tourIds relevant to a given path — used by the
 * header "Show help" menu so it can clear the right localStorage keys
 * and dispatch a reset event.
 *
 * Dashboard-family paths use the user's active role as the tourId (matching
 * how DashboardPage wires `useOnboarding(user.id, activeRole)`).
 */
const DASHBOARD_PATHS = new Set(['/dashboard', '/account', '/profile/edit', '/my-listings']);

export function getTourIdsForPath(pathname, activeRole) {
  if (DASHBOARD_PATHS.has(pathname) || pathname.startsWith('/admin')) {
    return activeRole ? [activeRole] : [];
  }
  const factory = pageIntroFactories[pathname];
  if (factory) {
    const content = factory(activeRole);
    return content?.tourId ? [content.tourId] : [];
  }
  return [];
}

/**
 * Settings-page HelpIcon copy — shared across all roles since these cards
 * appear on every role's Settings / Messaging tab.
 */
export const settingsHelp = {
  accountInformation: {
    title: 'Account Information',
    body: "Your name, email and username. Email is used for login and notifications — if you change it you'll be asked to verify the new address. Username is part of your public profile URL.",
  },
  security: {
    title: 'Security',
    body: "Change your password and review connected sign-in methods. We recommend a unique password you don't use elsewhere. We never email you your password.",
  },
  needHelp: {
    title: 'Need Help?',
    body: "Open a support ticket here and our team will respond within 48 hours. SLA timers on our side go red past 48h so nothing gets missed. You can also check the Help Centre for instant answers.",
  },
  notificationPreferences: {
    title: 'Notification Preferences',
    body: "Choose which in-app notifications you want to see — application updates, messages, profile views, new casting calls. These control the bell icon, not email. Email is managed separately in Email Preferences.",
  },
  emailPreferences: {
    title: 'Email Preferences',
    body: "Control which emails land in your inbox — weekly digest, profile-performance summaries, recommendation emails. You can unsubscribe all at once or pick specific categories. Transactional emails (password resets, receipts) are always sent.",
  },
  downloadMyData: {
    title: 'Download My Data',
    body: "Export a ZIP of everything we hold on you — profile details, applications, messages, saved items and activity log. GDPR-compliant. Generation usually takes under 60 seconds; you'll get an email when it's ready.",
  },
  messagingCommunication: {
    title: 'Messaging & Communication',
    body: "Control who can message you on the platform — everyone, verified users only, or only users you follow. Changes apply immediately. You can still block individual accounts from any conversation.",
  },
  contactAvailability: {
    title: 'Contact Availability',
    body: "Tell clients when you're generally available — e.g. weekdays, evenings, specific months. This appears on your public profile and helps casting directors reach out at the right time. It doesn't affect search ranking.",
  },
  professionalStatus: {
    title: 'Professional Status',
    body: "Set a status chip that appears on your profile — 'Available for work', 'Booked until May', 'Not currently taking on projects', etc. Clients see this instantly and it reduces unwanted enquiries when you're not free.",
  },
  subscriptionBilling: {
    title: 'Subscription & Billing',
    body: "View your current plan, next renewal date, and payment history. Upgrade or downgrade anytime — changes take effect from the next billing cycle. Cancel anytime; you keep access until the end of your current period.",
  },
  referralProgram: {
    title: 'Referral Program',
    body: "Invite friends using your unique link. Once their account is approved, you both get 14 free Pro days. Track everyone who has used your link on this page — no cap on rewards.",
  },
  dangerZone: {
    title: 'Delete your account',
    body: "Permanently deletes your account, profile, applications and messages. This is irreversible — if you just want a break, Archive (Settings → Archive my account) keeps everything recoverable for 90 days.",
  },
};
